import { useRef } from "react";
import {
  BLIND_TEXTURES,
  FRAME_TEXTURES,
  type TextureOption,
} from "@domain/constants/ralColors";

const FREE_STANDING_IMG = "/images/free_standing.png";
const ATTACHED_IMG = "/images/attached.png";
const MAX_BLIND_ROTATION = 135;

const MOUNTING_OPTIONS = [
  { label: "Free Standing", value: "free", img: FREE_STANDING_IMG },
  { label: "Attached", value: "attached", img: ATTACHED_IMG },
] as const;

const BLIND_PRESETS = [
  { label: "Closed", value: 0 },
  { label: "45°", value: 45 },
  { label: "90°", value: 90 },
  { label: "Open", value: MAX_BLIND_ROTATION },
] as const;

type TextureColorPickerProps = {
  title: string;
  textures: TextureOption[];
  selectedTexture: string;
  onTextureSelect: (texture: TextureOption) => void;
};

const TextureColorPicker = ({
  title,
  textures,
  selectedTexture,
  onTextureSelect,
}: TextureColorPickerProps) => (
  <div style={{ padding: "4px 0" }}>
    <p
      style={{
        textAlign: "center",
        fontSize: 15,
        color: "#444",
        margin: "0 0 16px",
      }}
    >
      Choose your <strong>{title.toLowerCase()}</strong>:
    </p>
    <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
      {textures.map((texture) => {
        const isSelected = selectedTexture === texture.img;

        return (
          <button
            key={texture.img}
            type="button"
            onClick={() => onTextureSelect(texture)}
            style={{
              flex: 1,
              maxWidth: 180,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              border: "none",
              background: "transparent",
              padding: 0,
            }}
          >
            <div
              style={{
                width: "100%",
                aspectRatio: "5 / 3",
                backgroundColor: texture.hex,
                borderRadius: 8,
                border: isSelected
                  ? "2.5px solid #71903e"
                  : "2px solid transparent",
                boxSizing: "border-box",
                transition: "border-color 0.2s",
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: isSelected ? 700 : 400,
                color: isSelected ? "#333" : "#888",
                textAlign: "center",
                transition: "color 0.2s",
              }}
            >
              {texture.name}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

type MountingStyleCardProps = {
  hasWall: boolean;
  onWallChange: (value: boolean) => void;
};

const MountingStyleCard = ({
  hasWall,
  onWallChange,
}: MountingStyleCardProps) => {
  const selected = hasWall ? "attached" : "free";

  return (
    <div style={{ padding: "4px 0" }}>
      <p
        style={{
          textAlign: "center",
          fontSize: 15,
          color: "#444",
          margin: "0 0 16px",
        }}
      >
        Choose your <strong>pergola</strong> design:
      </p>
      <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
        {MOUNTING_OPTIONS.map((option) => {
          const isSelected = selected === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onWallChange(option.value === "attached")}
              style={{
                flex: 1,
                maxWidth: 200,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                border: "none",
                background: "transparent",
                padding: 0,
              }}
            >
              <div
                style={{
                  width: "100%",
                  aspectRatio: "5 / 3",
                  borderRadius: 8,
                  border: isSelected
                    ? "2.5px solid #71903e"
                    : "2px solid #e0e0e0",
                  background: "#fafafa",
                  overflow: "hidden",
                  transition: "border-color 0.2s",
                  boxSizing: "border-box",
                }}
              >
                <img
                  src={option.img}
                  alt={option.label}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: isSelected ? 700 : 400,
                  color: isSelected ? "#333" : "#888",
                  transition: "color 0.2s",
                }}
              >
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

type BlindControlsCardProps = {
  rotation: number;
  onRotationChange: (value: number) => void;
};

const BlindControlsCard = ({
  rotation,
  onRotationChange,
}: BlindControlsCardProps) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const percentage = Math.round((rotation / MAX_BLIND_ROTATION) * 100);

  const updateRotationFromX = (clientX: number) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    const rect = track.getBoundingClientRect();
    const width = rect.width || 1;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / width));
    onRotationChange(Math.round(pct * MAX_BLIND_ROTATION));
  };

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    updateRotationFromX(event.clientX);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      updateRotationFromX(moveEvent.clientX);
    };

    const handlePointerEnd = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerEnd, { once: true });
    window.addEventListener("pointercancel", handlePointerEnd, { once: true });
  };

  return (
    <div style={{ padding: "4px 0" }}>
      <p
        style={{
          textAlign: "center",
          fontSize: 15,
          color: "#444",
          margin: "0 0 20px",
        }}
      >
        Adjust <strong>louver rotation</strong>:
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          marginBottom: 20,
        }}
      >
        <span
          style={{
            fontSize: 36,
            fontWeight: 700,
            color: "#71903e",
            lineHeight: 1,
          }}
        >
          {rotation}°
        </span>
        <span
          style={{
            fontSize: 13,
            color: "#999",
            marginTop: 6,
          }}
        >
          {rotation === 0
            ? "Closed"
            : rotation >= MAX_BLIND_ROTATION
              ? "Fully Open"
              : `${percentage}% open`}
        </span>
      </div>

      <div style={{ padding: "0 4px", marginBottom: 8 }}>
        <div
          ref={trackRef}
          style={{
            position: "relative",
            height: 6,
            background: "#e8e8e8",
            borderRadius: 3,
            cursor: "pointer",
            touchAction: "none",
          }}
          onPointerDown={handleTrackPointerDown}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${percentage}%`,
              background: "#71903e",
              borderRadius: 3,
              transition: "width 0.15s ease",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: `${percentage}%`,
              transform: "translate(-50%, -50%)",
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "#fff",
              border: "3px solid #71903e",
              boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
              transition: "left 0.15s ease",
            }}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "0 4px",
        }}
      >
        <span style={{ fontSize: 12, color: "#aaa" }}>Closed</span>
        <span style={{ fontSize: 12, color: "#aaa" }}>Open</span>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 16,
          justifyContent: "center",
        }}
      >
        {BLIND_PRESETS.map((preset) => {
          const isActive = rotation === preset.value;

          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => onRotationChange(preset.value)}
              style={{
                flex: 1,
                padding: "8px 0",
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#fff" : "#666",
                background: isActive ? "#71903e" : "#f5f5f5",
                border: isActive
                  ? "1.5px solid #71903e"
                  : "1.5px solid #e0e0e0",
                borderRadius: 6,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

type StyleTabControlsProps = {
  hasWall: boolean;
  frameTexture: string;
  blindTexture: string;
  blindRotation: number;
  onWallChange: (value: boolean) => void;
  onFrameTextureSelect: (texture: TextureOption) => void;
  onBlindTextureSelect: (texture: TextureOption) => void;
  onBlindRotationChange: (value: number) => void;
  gap?: number;
};

export const StyleTabControls = ({
  hasWall,
  frameTexture,
  blindTexture,
  blindRotation,
  onWallChange,
  onFrameTextureSelect,
  onBlindTextureSelect,
  onBlindRotationChange,
  gap = 32,
}: StyleTabControlsProps) => (
  <div style={{ display: "flex", flexDirection: "column", gap }}>
    <MountingStyleCard hasWall={hasWall} onWallChange={onWallChange} />

    <TextureColorPicker
      title="Structure color"
      textures={FRAME_TEXTURES}
      selectedTexture={frameTexture}
      onTextureSelect={onFrameTextureSelect}
    />

    <TextureColorPicker
      title="Louver color"
      textures={BLIND_TEXTURES}
      selectedTexture={blindTexture}
      onTextureSelect={onBlindTextureSelect}
    />

    <BlindControlsCard
      rotation={blindRotation}
      onRotationChange={onBlindRotationChange}
    />
  </div>
);
